import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createDischargeSummary,
  getDischargeSummaryByAdmission,
  listDischargeSummaryTemplates,
  listFollowUpInstructions,
  mapFormToSavePayload,
  mapTemplateToDropdownOption,
  searchDoses,
  searchDrugs,
  updateDischargeSummary,
} from "../../services/dischargeSummaryService";

const initialState = {
  patient: null,
  dischargeSummaryId: null,
  currentSummary: null,
  templates: [],
  followUpInstructions: [],
  drugs: [],
  doses: [],
  summaryHistory: [],
  loading: false,
  saving: false,
  templatesLoading: false,
  followUpInstructionsLoading: false,
  drugsLoading: false,
  dosesLoading: false,
  error: null,
};

export const fetchDischargeTemplates = createAsyncThunk(
  "dischargeSummary/fetchDischargeTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const rows = await listDischargeSummaryTemplates();
      return (rows || []).map(mapTemplateToDropdownOption);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchFollowUpInstructions = createAsyncThunk(
  "dischargeSummary/fetchFollowUpInstructions",
  async (_, { rejectWithValue }) => {
    try {
      return await listFollowUpInstructions();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const searchDischargeDrugs = createAsyncThunk(
  "dischargeSummary/searchDischargeDrugs",
  async (query, { rejectWithValue }) => {
    try {
      return await searchDrugs(query);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const searchDischargeDoses = createAsyncThunk(
  "dischargeSummary/searchDischargeDoses",
  async (query, { rejectWithValue }) => {
    try {
      return await searchDoses(query);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchDischargeSummaryByAdmission = createAsyncThunk(
  "dischargeSummary/fetchDischargeSummaryByAdmission",
  async (admissionId, { rejectWithValue }) => {
    try {
      return await getDischargeSummaryByAdmission(admissionId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveDischargeSummaryToApi = createAsyncThunk(
  "dischargeSummary/saveDischargeSummaryToApi",
  async ({ formData, patient, userId, dischargeSummaryId }, { rejectWithValue }) => {
    try {
      if (!patient?.admissionId) {
        return rejectWithValue("Please select a patient first");
      }
      const payload = mapFormToSavePayload(formData, patient, userId);
      if (dischargeSummaryId) {
        const result = await updateDischargeSummary(dischargeSummaryId, payload);
        return {
          result,
          formData,
          patient,
          savedAt: new Date().toISOString(),
          dischargeSummaryId,
        };
      }
      const result = await createDischargeSummary(payload);
      return {
        result,
        formData,
        patient,
        savedAt: new Date().toISOString(),
        dischargeSummaryId: result?.dischargeSummaryId ?? null,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to save discharge summary");
    }
  }
);

const dischargeSummarySlice = createSlice({
  name: "dischargeSummary",
  initialState,
  reducers: {
    setPatient: (state, action) => {
      state.patient = action.payload;
      if (!action.payload) {
        state.dischargeSummaryId = null;
        state.currentSummary = null;
      }
    },
    setDischargeSummaryId: (state, action) => {
      state.dischargeSummaryId = action.payload;
    },
    resetDischargeSummary: (state) => {
      state.patient = null;
      state.dischargeSummaryId = null;
      state.currentSummary = null;
      state.error = null;
      state.saving = false;
      state.loading = false;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDischargeTemplates.pending, (state) => {
        state.templatesLoading = true;
      })
      .addCase(fetchDischargeTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.templates = action.payload || [];
      })
      .addCase(fetchDischargeTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.error = action.payload || "Failed to load templates";
        state.templates = [];
      })
      .addCase(fetchFollowUpInstructions.pending, (state) => {
        state.followUpInstructionsLoading = true;
      })
      .addCase(fetchFollowUpInstructions.fulfilled, (state, action) => {
        state.followUpInstructionsLoading = false;
        state.followUpInstructions = action.payload || [];
      })
      .addCase(fetchFollowUpInstructions.rejected, (state, action) => {
        state.followUpInstructionsLoading = false;
        state.error = action.payload || "Failed to load follow-up instructions";
        state.followUpInstructions = [];
      })
      .addCase(searchDischargeDrugs.pending, (state) => {
        state.drugsLoading = true;
      })
      .addCase(searchDischargeDrugs.fulfilled, (state, action) => {
        state.drugsLoading = false;
        state.drugs = action.payload || [];
      })
      .addCase(searchDischargeDrugs.rejected, (state, action) => {
        state.drugsLoading = false;
        state.error = action.payload || "Failed to search drugs";
      })
      .addCase(searchDischargeDoses.pending, (state) => {
        state.dosesLoading = true;
      })
      .addCase(searchDischargeDoses.fulfilled, (state, action) => {
        state.dosesLoading = false;
        state.doses = action.payload || [];
      })
      .addCase(searchDischargeDoses.rejected, (state, action) => {
        state.dosesLoading = false;
        state.error = action.payload || "Failed to search doses";
      })
      .addCase(fetchDischargeSummaryByAdmission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDischargeSummaryByAdmission.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSummary = action.payload;
        state.dischargeSummaryId = action.payload?.dischargeSummaryId ?? null;
      })
      .addCase(fetchDischargeSummaryByAdmission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load discharge summary";
        state.currentSummary = null;
        state.dischargeSummaryId = null;
      })
      .addCase(saveDischargeSummaryToApi.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveDischargeSummaryToApi.fulfilled, (state, action) => {
        state.saving = false;
        const id =
          action.payload?.dischargeSummaryId ||
          action.payload?.result?.dischargeSummaryId ||
          null;
        if (id) state.dischargeSummaryId = id;
        state.summaryHistory.unshift({
          id: id || Date.now(),
          patient: action.payload.patient,
          formData: action.payload.formData,
          savedAt: action.payload.savedAt,
        });
      })
      .addCase(saveDischargeSummaryToApi.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to save discharge summary";
      });
  },
});

export const {
  setPatient,
  setDischargeSummaryId,
  resetDischargeSummary,
  setLoading,
} = dischargeSummarySlice.actions;

export default dischargeSummarySlice.reducer;
